// Staff-friendly wizard rendered in place of the generic workflow panel when
// the active workflow is `class_creation`.
//
// Design rules enforced here:
//   • Never display UUIDs, slugs, or internal identifiers to the user.
//   • Always identify the class by its human-readable title.
//   • Explain why the card is appearing (draft, unfinished setup, etc.).
//   • Read checklist state from the SAVED class record when we can resolve
//     one — the workflow draft is only a fallback overlay.
//   • If we cannot confidently identify which class this refers to, show a
//     quiet "No unfinished class setups." card instead of a misleading one.
import { useEffect, useMemo, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { friendlyError } from "@/lib/friendlyError";
import {
  ChevronDown,
  ChevronUp,
  Save,
  X,
  Loader2,
  CheckCircle2,
  Circle,
  Sparkles,
  AlertTriangle,
} from "lucide-react";
import type { WorkflowView } from "./CurrentWorkflowPanel";

type Busy = null | "create" | "save" | "cancel";

interface Props {
  workflow: WorkflowView;
  functionName: string;
  onChange: (workflow: WorkflowView | null) => void;
  onSendMessage?: (text: string) => void;
  forceOpenNonce?: number;
}

interface ChecklistItem {
  key: string;
  label: string;
  helper: string;
  done: boolean;
  required: boolean;
  prompt: string;
}

interface SavedClass {
  id: string;
  title: string | null;
  status: string | null;
  instructor_id: string | null;
  location: string | null;
  capacity: number | null;
  price_per_child: number | null;
  schedule: string | null;
  preferred_days: string | null;
  preferred_time: string | null;
  published_at: string | null;
  updated_at: string | null;
}

const DAY_LABELS = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function isUuid(v: unknown): v is string {
  return typeof v === "string" && UUID_RE.test(v);
}

function relativeTime(iso: string | null | undefined): string {
  if (!iso) return "recently";
  const then = new Date(iso).getTime();
  if (!Number.isFinite(then)) return "recently";
  const diffMs = Date.now() - then;
  const mins = Math.round(diffMs / 60_000);
  if (mins < 2) return "just now";
  if (mins < 60) return `${mins} minutes ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs} hour${hrs === 1 ? "" : "s"} ago`;
  const days = Math.round(hrs / 24);
  if (days === 1) return "yesterday";
  if (days < 14) return `${days} days ago`;
  const weeks = Math.round(days / 7);
  return `${weeks} week${weeks === 1 ? "" : "s"} ago`;
}

export function ClassCreationWizard({
  workflow,
  functionName,
  onChange,
  onSendMessage,
  forceOpenNonce = 0,
}: Props) {
  const [open, setOpen] = useState(true);
  const [busy, setBusy] = useState<Busy>(null);
  const [saved, setSaved] = useState<SavedClass | null>(null);
  const [savedLoading, setSavedLoading] = useState(false);

  useEffect(() => {
    if (forceOpenNonce > 0) setOpen(true);
  }, [forceOpenNonce]);

  const c = workflow.collected ?? {};
  const classId = isUuid(c.class_id) ? (c.class_id as string) : null;

  // Load saved class record (source of truth). The draft is only an overlay.
  useEffect(() => {
    let cancelled = false;
    if (!classId) {
      setSaved(null);
      return;
    }
    setSavedLoading(true);
    (async () => {
      const { data } = await (supabase as unknown as {
        from: (t: string) => {
          select: (s: string) => {
            eq: (c: string, v: string) => { maybeSingle: () => Promise<{ data: SavedClass | null }> };
          };
        };
      })
        .from("classes")
        .select(
          "id,title,status,instructor_id,location,capacity,price_per_child,schedule,preferred_days,preferred_time,published_at,updated_at",
        )
        .eq("id", classId)
        .maybeSingle();
      if (!cancelled) {
        setSaved(data ?? null);
        setSavedLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [classId]);

  // Human-readable title, sourced (in order) from: saved record, draft-collected
  // title, else nothing. We NEVER fall back to an id/slug/UUID.
  const displayTitle: string | null = useMemo(() => {
    const fromSaved = saved?.title?.trim();
    if (fromSaved) return fromSaved;
    const fromDraft = typeof c.class_title === "string" ? c.class_title.trim() : "";
    if (fromDraft) return fromDraft;
    return null;
  }, [saved, c.class_title]);

  const completed = workflow.status === "completed";

  // ---- Confidence gate ----
  // If we can't identify the class with confidence (no saved record found AND
  // no human-readable title in the draft), don't show a misleading checklist.
  const cannotIdentify =
    !savedLoading && !displayTitle && (classId ? saved === null : true);

  // Merged view: for each field, "done" means the SAVED record has a value.
  // Draft-collected values are used as a fallback only when there is no saved
  // record yet (brand-new class still being drafted in chat).
  const has = (v: unknown) => v !== null && v !== undefined && v !== "";
  const savedDayLabel = useMemo(() => {
    // preferred_days like "Monday" or "Mon" — use as-is if present.
    return saved?.preferred_days?.trim() || "";
  }, [saved]);
  const draftDayLabel = typeof c.day_of_week === "number" ? DAY_LABELS[c.day_of_week] : "";

  const checklist = useMemo<ChecklistItem[]>(() => {
    const title = displayTitle ?? "this class";

    const titleDone = has(saved?.title) || has(c.class_title);
    const titleHelper = (saved?.title || (c.class_title as string) || "What families will see on the card.") as string;

    const scheduleDone = saved
      ? has(saved.schedule) || (has(saved.preferred_days) && has(saved.preferred_time))
      : has(c.day_of_week) && has(c.start_time) && has(c.end_time);
    const scheduleHelper = saved?.schedule
      ? saved.schedule
      : [savedDayLabel || draftDayLabel, c.start_time, c.end_time && `– ${c.end_time}`]
          .filter(Boolean)
          .join(" ") || "Day of week, start time, and end time.";

    const instructorDone = has(saved?.instructor_id) || has(c.instructor_query);
    const instructorHelper = saved?.instructor_id
      ? "Assigned"
      : has(c.instructor_query)
      ? String(c.instructor_query)
      : "Who will be teaching this class.";

    const locationDone = has(saved?.location) || has(c.location_override);
    const locationHelper = (saved?.location as string) || (c.location_override as string) || "Where families should arrive.";

    const capacityDone = has(saved?.capacity) || has(c.capacity_override);
    const capacityValue = saved?.capacity ?? c.capacity_override;
    const capacityHelper = has(capacityValue) ? `${capacityValue} students` : "Maximum students per session.";

    const payDone = has(c.pay_rate_amount);
    const payHelper = has(c.pay_rate_amount)
      ? `$${c.pay_rate_amount}${c.pay_rate_type ? ` / ${c.pay_rate_type}` : ""}`
      : "Rate you'll pay the instructor.";

    const sessionDone = has(c.session_label);
    const sessionHelper = has(c.session_label)
      ? String(c.session_label)
      : "Optional — e.g. \"Fall Semester\" or \"Session 1\".";

    return [
      { key: "class_title", label: "Class title", helper: titleHelper, done: titleDone, required: true, prompt: `The class title is ` },
      { key: "schedule", label: "Schedule", helper: scheduleHelper, done: scheduleDone, required: true, prompt: `Set the schedule for ${title} — it meets on ` },
      { key: "instructor", label: "Instructor", helper: instructorHelper, done: instructorDone, required: true, prompt: `The instructor for ${title} is ` },
      { key: "location_override", label: "Location", helper: locationHelper, done: locationDone, required: true, prompt: `The location for ${title} is ` },
      { key: "capacity_override", label: "Capacity", helper: capacityHelper, done: capacityDone, required: true, prompt: `Set the capacity for ${title} to ` },
      { key: "pay_rate_amount", label: "Instructor pay", helper: payHelper, done: payDone, required: false, prompt: `Set the instructor pay for ${title} to ` },
      { key: "session_label", label: "Session label", helper: sessionHelper, done: sessionDone, required: false, prompt: `The session label for ${title} is ` },
    ];
  }, [displayTitle, saved, c, savedDayLabel, draftDayLabel]);

  const requiredItems = checklist.filter((i) => i.required);
  const optionalItems = checklist.filter((i) => !i.required);
  const requiredDone = requiredItems.filter((i) => i.done).length;
  const requiredMissing = requiredItems.filter((i) => !i.done);
  const optionalMissing = optionalItems.filter((i) => !i.done);
  const readyToPublish = requiredMissing.length === 0;
  const percent = Math.round((requiredDone / Math.max(1, requiredItems.length)) * 100);

  const errorCount = workflow.validation.filter((v) => v.level === "error").length;
  const validationBlocked = errorCount > 0;
  const needsReviewGate = workflow.status === "draft";

  // Status/why sentence
  const savedStatus = saved?.status ?? null;
  const isSavedDraft = savedStatus === "draft";
  const isPublished = savedStatus === "published" || completed;
  const startedWhen = relativeTime(workflow.updated_at);
  const whySentence = useMemo(() => {
    if (isPublished) return "This class is published.";
    if (isSavedDraft) {
      return requiredMissing.length > 0
        ? `Saved as a draft — it needs a few more details before it can be published.`
        : `Saved as a draft and ready to publish when you are.`;
    }
    if (saved) {
      return requiredMissing.length > 0
        ? `This class is missing information before it can be published.`
        : `Every parent-facing detail is filled in — ready to publish.`;
    }
    // Draft-only (no saved record yet)
    return `You started creating this class ${startedWhen} but haven't finished it.`;
  }, [isPublished, isSavedDraft, saved, requiredMissing.length, startedWhen]);

  const statusBadge = isPublished
    ? "Published"
    : isSavedDraft
    ? "Draft"
    : saved
    ? "Unpublished"
    : "Unfinished setup";

  const call = async (action: string, extra: Record<string, unknown> = {}) => {
    const { data, error } = await supabase.functions.invoke(functionName, {
      body: { action, draft_id: workflow.id, ...extra },
    });
    if (error) throw new Error(error.message);
    return data;
  };

  const doCreate = async () => {
    setBusy("create");
    try {
      if (needsReviewGate) {
        const r = await call("workflow_prepare_review");
        onChange(r?.workflow ?? null);
      }
      const r = await call("workflow_execute");
      onChange(r?.view ?? null);
      const results = r?.results ?? [];
      const failed = results.filter((x: { ok: boolean }) => !x.ok);
      if (failed.length === 0) {
        toast.success(`${displayTitle ?? "This class"} is published.`);
      } else {
        friendlyError({
          what: `I couldn't finish setting up ${displayTitle ?? "this class"}.`,
          meaning: "Some steps didn't complete.",
          changed: "The completed steps were saved.",
          nextStep: "Review the flagged item below and try again.",
          technical: failed[0].error,
        });
      }
    } catch (e: unknown) {
      friendlyError({ what: `I couldn't publish ${displayTitle ?? "this class"} yet.`, technical: e });
    } finally {
      setBusy(null);
    }
  };

  const doSave = async () => {
    setBusy("save");
    try {
      const r = await call("workflow_save");
      onChange(r?.workflow ?? null);
      toast.success("Saved. Pick up where you left off any time.");
    } catch (e: unknown) {
      friendlyError({ what: "I couldn't save this class as a draft.", technical: e });
    } finally {
      setBusy(null);
    }
  };

  const doCancel = async () => {
    const label = displayTitle ?? "this unfinished setup";
    if (!confirm(`Discard the setup for ${label}? Nothing will be saved.`)) return;
    setBusy("cancel");
    try {
      await call("workflow_cancel");
      onChange(null);
    } catch (e: unknown) {
      friendlyError({ what: `I couldn't discard the setup for ${label}.`, technical: e });
    } finally {
      setBusy(null);
    }
  };

  const sendPrompt = (text: string) => {
    if (onSendMessage) onSendMessage(text);
    else toast.info("Type your next instruction in the chat below.");
  };

  // ---- Render: cannot-identify quiet card ----
  if (cannotIdentify) {
    return (
      <Card className="border-border bg-muted/30">
        <div className="px-3 py-3 flex items-center gap-2">
          <Badge variant="outline" className="text-[10px] uppercase">Class Setup</Badge>
          <div className="text-sm text-muted-foreground flex-1">No unfinished class setups.</div>
          <Button
            size="sm"
            variant="ghost"
            onClick={doCancel}
            disabled={!!busy}
            aria-label="Dismiss"
            className="h-7"
          >
            {busy === "cancel" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <X className="h-3.5 w-3.5" />}
          </Button>
        </div>
      </Card>
    );
  }

  return (
    <Card className="border-primary/30 bg-primary/5">
      {/* Header — human-readable title only, never an id */}
      <div className="flex items-center gap-2 px-3 py-2 border-b border-primary/20">
        <Badge variant="outline" className="text-[10px] uppercase">Class Setup</Badge>
        <div className="font-medium text-sm truncate flex-1">
          {isPublished
            ? `${displayTitle} — published`
            : displayTitle
            ? `Continue setting up "${displayTitle}"`
            : "Continue setting up this class"}
        </div>
        <Badge variant="secondary" className="text-[10px]">{statusBadge}</Badge>
        <span className="text-[11px] text-muted-foreground tabular-nums">{percent}%</span>
        <Button
          variant="ghost"
          size="icon"
          className="h-6 w-6"
          onClick={() => setOpen(!open)}
          aria-label={open ? "Collapse" : "Expand"}
        >
          {open ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
        </Button>
      </div>

      {/* Why sentence */}
      <div className="px-3 pt-2 text-[11px] text-muted-foreground">
        {whySentence}
      </div>

      <div className="px-3 pt-2">
        <Progress value={percent} className="h-1.5" />
      </div>

      {open && (
        <div className="px-3 py-3 space-y-3 text-sm">
          {readyToPublish && !isPublished && (
            <div className="rounded border border-emerald-300 bg-emerald-50 text-emerald-900 px-3 py-2 text-xs flex items-start gap-2">
              <Sparkles className="h-3.5 w-3.5 mt-0.5 shrink-0" />
              <span>
                <strong>Ready to publish.</strong> Every parent-facing detail is filled in.
                You can polish description, pricing, and cover image any time from the class editor.
              </span>
            </div>
          )}

          {validationBlocked && (
            <div className="space-y-1">
              {workflow.validation
                .filter((v) => v.level === "error")
                .map((v, i) => (
                  <div key={i} className="text-xs flex gap-1.5 text-destructive">
                    <AlertTriangle className="h-3 w-3 mt-0.5 shrink-0" /> {v.message}
                  </div>
                ))}
            </div>
          )}

          <div>
            <div className="text-xs font-semibold text-muted-foreground uppercase mb-1.5">
              Needed to publish
            </div>
            <ul className="space-y-1">
              {requiredItems.map((item) => (
                <ChecklistRow key={item.key} item={item} onClick={() => sendPrompt(item.prompt)} />
              ))}
            </ul>
          </div>

          {optionalItems.length > 0 && (
            <div>
              <div className="text-xs font-semibold text-muted-foreground uppercase mb-1.5">
                Nice to add
              </div>
              <ul className="space-y-1">
                {optionalItems.map((item) => (
                  <ChecklistRow key={item.key} item={item} onClick={() => sendPrompt(item.prompt)} />
                ))}
              </ul>
            </div>
          )}

          <div className="flex flex-wrap gap-2 pt-1 border-t mt-2 pt-3">
            {!isPublished && (
              <Button
                size="sm"
                onClick={doCreate}
                disabled={!readyToPublish || validationBlocked || !!busy}
              >
                {busy === "create" ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" />
                ) : (
                  <Sparkles className="h-3.5 w-3.5 mr-1" />
                )}
                Publish class
              </Button>
            )}
            {!isPublished && (
              <Button size="sm" variant="outline" onClick={doSave} disabled={!!busy}>
                {busy === "save" ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" />
                ) : (
                  <Save className="h-3.5 w-3.5 mr-1" />
                )}
                Save draft
              </Button>
            )}
            {!isPublished && (
              <Button size="sm" variant="ghost" onClick={doCancel} disabled={!!busy}>
                <X className="h-3.5 w-3.5 mr-1" /> Discard
              </Button>
            )}
          </div>

          {!readyToPublish && !isPublished && (
            <p className="text-[11px] text-muted-foreground">
              {requiredMissing.length} item{requiredMissing.length === 1 ? "" : "s"} left before this
              class is ready to publish
              {optionalMissing.length > 0
                ? ` · ${optionalMissing.length} optional detail${optionalMissing.length === 1 ? "" : "s"} you can add`
                : ""}
              .
            </p>
          )}
        </div>
      )}
    </Card>
  );
}

function ChecklistRow({ item, onClick }: { item: ChecklistItem; onClick: () => void }) {
  return (
    <li>
      <button
        type="button"
        onClick={onClick}
        className={`w-full text-left rounded border px-2.5 py-2 flex items-start gap-2 transition-colors ${
          item.done
            ? "border-emerald-200 bg-emerald-50/60 hover:bg-emerald-50"
            : "border-border bg-background hover:border-primary/40 hover:bg-primary/5"
        }`}
      >
        {item.done ? (
          <CheckCircle2 className="h-4 w-4 text-emerald-600 shrink-0 mt-0.5" />
        ) : (
          <Circle className="h-4 w-4 text-muted-foreground shrink-0 mt-0.5" />
        )}
        <div className="min-w-0 flex-1">
          <div
            className={`text-xs font-medium ${
              item.done ? "text-emerald-900" : "text-foreground"
            }`}
          >
            {item.label}
          </div>
          <div
            className={`text-[11px] mt-0.5 truncate ${
              item.done ? "text-emerald-800/80" : "text-muted-foreground"
            }`}
          >
            {item.helper}
          </div>
        </div>
      </button>
    </li>
  );
}
